import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { api } from "../../lib/api";
import { humanise } from "../../lib/format";
import { Icon } from "../ui/Icon";
import { Pill, statusTone } from "../ui/primitives";

interface SearchHit {
  kind: string;
  id: string;
  title: string;
  subtitle: string | null;
  badge: string | null;
  moduleKey: string;
  eventId: string | null;
  eventName: string | null;
}

interface SearchResults {
  query: string;
  totalShown: number;
  hits: SearchHit[];
}

interface QuickAction {
  key: string;
  label: string;
  description: string;
  moduleKey: string;
  requiredPermission: string;
}

/** Where each kind of record lives in the application. */
const ROUTE_FOR: Record<string, (hit: SearchHit) => string> = {
  Event: (hit) => `/events/${hit.id}`,
  Person: (hit) => `/people/${hit.id}`,
  Organization: (hit) => `/clients/${hit.id}`,
  WorkItem: (hit) => `/work/${hit.id}`,
  Document: (hit) => `/documents?highlight=${hit.id}`,
  Registration: (hit) => `/registration/${hit.id}`,
  Sponsorship: (hit) => `/sponsors/${hit.id}`,
  Invoice: (hit) => `/finance/invoices/${hit.id}`,
};

const ACTION_ROUTE: Record<string, string> = {
  "new-event": "/events?create=1",
  "new-task": "/work?create=task",
  "new-incident": "/work?create=incident",
  "new-risk": "/work?create=risk",
  "new-person": "/people?create=1",
  "new-opportunity": "/pipeline?create=1",
  "new-sponsor": "/sponsors?create=1",
  "new-invoice": "/finance?create=invoice",
  "upload-document": "/documents?upload=1",
  "new-campaign": "/communications?create=1",
};

const ICON_FOR: Record<string, keyof typeof Icon> = {
  Event: "calendar",
  Person: "user",
  Organization: "building",
  WorkItem: "checkSquare",
  Document: "file",
  Registration: "ticket",
  Sponsorship: "handshake",
  Invoice: "money",
};

/**
 * The command palette.
 *
 * Results come from the backend's cross-module search, which only queries the
 * sources the caller is permitted to read — so the palette cannot reveal that a
 * record exists in a module the person cannot open. Quick actions are filtered
 * the same way.
 */
export function CommandPalette({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 180);
    return () => clearTimeout(id);
  }, [query]);

  const { data: results, isFetching } = useQuery({
    queryKey: ["search", debounced],
    queryFn: () => api.get<SearchResults>("/search", { q: debounced }),
    enabled: debounced.length >= 2,
  });

  const { data: actions } = useQuery({
    queryKey: ["quick-actions"],
    queryFn: () => api.get<QuickAction[]>("/search/quick-actions"),
    staleTime: 5 * 60_000,
  });

  const rows = useMemo(() => {
    if (debounced.length >= 2) {
      return (results?.hits ?? []).map((hit) => ({
        id: `${hit.kind}:${hit.id}`,
        icon: ICON_FOR[hit.kind] ?? "list",
        title: hit.title,
        subtitle: [hit.subtitle, hit.eventName].filter(Boolean).join(" · ") || null,
        badge: hit.badge,
        kind: humanise(hit.kind),
        go: () => navigate(ROUTE_FOR[hit.kind]?.(hit) ?? "/"),
      }));
    }

    return (actions ?? []).map((action) => ({
      id: action.key,
      icon: "plus" as keyof typeof Icon,
      title: action.label,
      subtitle: action.description,
      badge: null as string | null,
      kind: "Create",
      go: () => navigate(ACTION_ROUTE[action.key] ?? "/"),
    }));
  }, [debounced, results, actions, navigate]);

  // Adjusted during render rather than in an effect: an effect would let one
  // frame paint with the cursor pointing at a row that is no longer there.
  // This is React's documented pattern for state derived from a changing input.
  const [rowCountAtCursor, setRowCountAtCursor] = useState(rows.length);

  if (rowCountAtCursor !== rows.length) {
    setRowCountAtCursor(rows.length);
    setCursor(0);
  }

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        setCursor((c) => Math.min(c + 1, Math.max(0, rows.length - 1)));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setCursor((c) => Math.max(0, c - 1));
      } else if (event.key === "Enter" && rows[cursor]) {
        event.preventDefault();
        rows[cursor].go();
        onClose();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [rows, cursor, onClose]);

  return createPortal(
    <div
      className="scrim"
      style={{ alignItems: "flex-start", paddingTop: "12vh" }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="modal"
        role="dialog"
        aria-label="Command palette"
        style={{ width: "min(660px, 100%)" }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 11,
            padding: "14px 16px",
            borderBottom: "1px solid var(--line)",
          }}
        >
          <Icon.search width={17} height={17} style={{ color: "var(--text-muted)" }} />
          <input
            ref={inputRef}
            className="input"
            style={{ border: "none", background: "transparent", padding: 0, fontSize: 15 }}
            placeholder="Search events, people, partners, work…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Search everything"
          />
          {isFetching && <span className="spinner" />}
          <kbd className="topbar__kbd" style={{ position: "static", transform: "none" }}>
            ESC
          </kbd>
        </div>

        <div className="modal__body" style={{ padding: 8, maxHeight: 420 }}>
          {debounced.length >= 2 && rows.length === 0 && !isFetching && (
            <p
              className="muted"
              style={{ padding: "26px 12px", textAlign: "center", font: "var(--t-small)" }}
            >
              Nothing matched “{debounced}”.
            </p>
          )}

          {debounced.length < 2 && (
            <div className="navgroup__label" style={{ padding: "6px 10px 8px" }}>
              Quick create
            </div>
          )}

          {rows.map((row, index) => {
            const RowIcon = Icon[row.icon];

            return (
              <button
                key={row.id}
                className="navlink"
                style={{
                  background: index === cursor ? "var(--ink-700)" : undefined,
                  alignItems: "flex-start",
                  padding: "10px 11px",
                }}
                onMouseEnter={() => setCursor(index)}
                onClick={() => {
                  row.go();
                  onClose();
                }}
              >
                <RowIcon className="navlink__icon" style={{ marginTop: 2 }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span
                    style={{
                      display: "block",
                      font: "600 13px/1.35 var(--font)",
                      color: "var(--text)",
                    }}
                  >
                    {row.title}
                  </span>
                  {row.subtitle && (
                    <span
                      className="muted truncate"
                      style={{ display: "block", font: "var(--t-small)" }}
                    >
                      {row.subtitle}
                    </span>
                  )}
                </span>
                {row.badge && (
                  <Pill tone={statusTone(row.badge)}>{humanise(row.badge)}</Pill>
                )}
                <span className="muted" style={{ font: "var(--t-small)" }}>
                  {row.kind}
                </span>
              </button>
            );
          })}
        </div>

        <footer
          className="modal__foot"
          style={{ justifyContent: "flex-start", gap: 16, font: "var(--t-small)" }}
        >
          <span className="muted">↑↓ to move</span>
          <span className="muted">↵ to open</span>
          <span className="muted">esc to close</span>
        </footer>
      </div>
    </div>,
    document.body,
  );
}
